import {
  ScrollArea as MantineScrollArea,
  ScrollAreaProps,
  Box,
} from "@mantine/core"
import { useReducedMotion } from "@mantine/hooks"
import {
  IconChevronDown,
  IconChevronUp,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react"
import { forwardRef, PropsWithChildren, useRef } from "react"

import { stepScroll } from "../../hooks/scrollStep"
import { useDragScroll } from "../../hooks/useDragScroll"
import {
  ScrollDirection,
  ScrollDirectionType,
  useScroll,
} from "../../hooks/useScroll"
import { useZoom } from "../../hooks/useZoom"
import { ZoomContainer, ZoomControls } from "../ZoomContainer/ZoomContainer"

import classes from "./ScrollArea.module.css"

const STEP_ARIA_LABEL: Record<ScrollDirectionType, string> = {
  left: "Previous column",
  right: "Next column",
  top: "Previous row",
  bottom: "Next row",
}

type EnhancedScrollAreaProps = ScrollAreaProps &
  PropsWithChildren & {
    showEdgeFade?: boolean
    zoomable?: boolean
    hideScrollbar?: boolean
    dragScroll?: boolean
    /** CSS selector for card-like nodes used when stepping one block at a time. */
    stepTargetSelector?: string
  }

export const ScrollArea = forwardRef<HTMLDivElement, EnhancedScrollAreaProps>(
  (
    {
      children,
      showEdgeFade = false,
      zoomable = false,
      hideScrollbar = false,
      dragScroll = false,
      stepTargetSelector = "td",
      style,
      styles,
      h,
      mah,
      className,
      ...props
    },
    ref
  ) => {
    const scrollViewportRef = useRef<HTMLDivElement>(null)
    const { overflow } = useScroll(scrollViewportRef)
    const prefersReducedMotion = useReducedMotion()
    const zoomApi = useZoom()
    const useStageLayout = showEdgeFade || dragScroll || zoomable

    useDragScroll(scrollViewportRef, dragScroll)

    const handleStep = (direction: ScrollDirectionType) => {
      const canvas = scrollViewportRef.current
      if (!canvas) return
      stepScroll({
        canvas,
        direction,
        stepTargetSelector,
        prefersReducedMotion: Boolean(prefersReducedMotion),
      })
    }

    const viewportStyles = {
      ...(useStageLayout
        ? {
            overscrollBehavior: "contain" as const,
            scrollBehavior: (prefersReducedMotion ? "auto" : "smooth") as
              | "auto"
              | "smooth",
          }
        : {}),
      ...(typeof styles === "object" && styles && "viewport" in styles
        ? styles.viewport
        : {}),
    }

    const content = zoomable ? (
      <ZoomContainer zoom={zoomApi.zoom}>{children}</ZoomContainer>
    ) : useStageLayout ? (
      <div className={classes.grid}>{children}</div>
    ) : (
      children
    )

    const scrollArea = (
      <MantineScrollArea
        {...props}
        h={useStageLayout ? "100%" : h}
        mah={useStageLayout ? undefined : mah}
        viewportRef={scrollViewportRef}
        ref={ref}
        className={
          useStageLayout
            ? `${classes.canvas}${className ? ` ${className}` : ""}`
            : className
        }
        style={{
          ...(dragScroll ? { cursor: "grab" } : {}),
          ...style,
        }}
        styles={{
          ...(typeof styles === "object" && styles ? styles : {}),
          viewport: viewportStyles,
          ...(hideScrollbar
            ? {
                scrollbar: {
                  display: "none",
                },
              }
            : {}),
        }}
      >
        {content}
      </MantineScrollArea>
    )

    if (!useStageLayout) {
      return scrollArea
    }

    return (
      <Box
        className={classes.stage}
        h={h}
        mah={mah}
        data-reduced-motion={prefersReducedMotion ? "true" : undefined}
      >
        {scrollArea}

        {zoomable ? (
          <ZoomControls
            zoom={zoomApi.zoom}
            zoomIndex={zoomApi.zoomIndex}
            onZoomIn={zoomApi.handleZoomIn}
            onZoomOut={zoomApi.handleZoomOut}
          />
        ) : null}

        {showEdgeFade ? (
          <>
            <div
              className={`${classes.fade} ${classes.fadeTop}`}
              data-visible={overflow.top ? "true" : "false"}
              aria-hidden
            />
            <div
              className={`${classes.fade} ${classes.fadeBottom}`}
              data-visible={overflow.bottom ? "true" : "false"}
              aria-hidden
            />
            <div
              className={`${classes.fade} ${classes.fadeLeft}`}
              data-visible={overflow.left ? "true" : "false"}
              aria-hidden
            />
            <div
              className={`${classes.fade} ${classes.fadeRight}`}
              data-visible={overflow.right ? "true" : "false"}
              aria-hidden
            />

            {(
              [
                ScrollDirection.TOP,
                ScrollDirection.BOTTOM,
                ScrollDirection.LEFT,
                ScrollDirection.RIGHT,
              ] as const
            ).map((direction) =>
              overflow[direction] ? (
                <StepButton
                  key={direction}
                  direction={direction}
                  onClick={() => handleStep(direction)}
                />
              ) : null
            )}
          </>
        ) : null}
      </Box>
    )
  }
)

export const ScrollIcons: Record<ScrollDirectionType, React.ReactNode> = {
  left: <IconChevronLeft size={20} />,
  right: <IconChevronRight size={20} />,
  top: <IconChevronUp size={20} />,
  bottom: <IconChevronDown size={20} />,
}

interface StepButtonProps {
  direction: ScrollDirectionType
  onClick: () => void
}

const STEP_CLASS: Record<ScrollDirectionType, string> = {
  left: classes.stepLeft,
  right: classes.stepRight,
  top: classes.stepTop,
  bottom: classes.stepBottom,
}

const StepButton = ({ direction, onClick }: StepButtonProps) => {
  return (
    <button
      type="button"
      aria-label={STEP_ARIA_LABEL[direction]}
      onClick={onClick}
      className={`${classes.step} ${STEP_CLASS[direction]}`}
    >
      {ScrollIcons[direction]}
    </button>
  )
}

ScrollArea.displayName = "ScrollArea"
